import { useAuth } from './useAuth';

/**
 * The IANA time zone in which day boundaries and clock times are resolved on the
 * trips surface — the TENANT's zone from `/api/me`, threaded here so every viewer
 * and every CI runner sees the same day for the same trip.
 *
 * THE FALLBACK, and why it is UTC and not the browser's zone:
 *
 * The reported defect was that days resolved in *whichever machine was looking* —
 * EAT on Mahad's phone, UTC on CI, and wrong for a Zambian tenant. Reading
 * `Intl.DateTimeFormat().resolvedOptions().timeZone` (the browser zone) as a
 * fallback would REINTRODUCE exactly that bug: it looks correct in Mogadishu and
 * is silently wrong everywhere else. So when the tenant's zone is absent — only
 * while the backend half of the `/me.timezone` contract is still in flight — we
 * fall back to **UTC**: deterministic, identical on every machine, and honest in
 * that it does not claim to be anyone's local zone. It is deliberately NOT
 * hardcoded to `Africa/Mogadishu`, which would be a guess that rots the moment a
 * non-Somali tenant signs in.
 *
 * `isFallback` is exposed so a caller that DID render a zone label could refuse to
 * present UTC as the tenant's own zone. The trips screens render no zone label —
 * every time on them is already shown in this one zone — so they consume only
 * `timeZone`.
 */
export function useTenantTimeZone(): { timeZone: string; isFallback: boolean } {
  const { state } = useAuth();
  const tz = state.status === 'authenticated' ? state.user.timezone : undefined;
  if (tz && tz.trim() !== '') return { timeZone: tz, isFallback: false };
  return { timeZone: 'UTC', isFallback: true };
}
